<!-- eslint-disable vue/no-v-html -->
<template>
  <div v-if="label || title" class="BookingStepper">
    <ai-typo
      v-if="label"
      variant="paragraph-02-regular"
      as="span"
      color="stratos-700"
      class="BookingStepper-label"
      aria-live="polite"
      aria-current="step">
      <span v-html="label" />
    </ai-typo>

    <div v-if="title" class="BookingStepper-title">
      <ai-typo as="h2" variant="heading-01">
        {{ title }}
      </ai-typo>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBookingStore } from '../../store';

const bookingStore = useBookingStore();
const { currentStep } = useCurrentBookingStep();
const { t, locale } = useI18n();

const multiple = computed(() => bookingStore.numberOfRooms > 1);
const currentRoomBeingFilled = computed(() => bookingStore.rooms.length + 1);
const currentRoomGuestCount = computed(
  () => bookingStore.currentUnconfiguredGuestsCount,
);

let lastUsedTitle: string | undefined = '';
let lastUsedLabel: string | undefined = '';
let translationCache: any = {};

const title = ref<any>('');
const label = ref<any>('');

const previousStep = ref('');

const stepIndexes = new Map([
  ['room', 1],
  ['rate', 2],
  ['details', 3],
  ['payment', 4],
]);

function translate(text: any, callback: any) {
  if (
    process.server ||
    !text ||
    locale.value === 'en' ||
    !(window as any).Weglot
  ) {
    if (callback) {
      callback(text);
    }
    return;
  }

  if (translationCache[text]) {
    if (callback) {
      callback(translationCache[text]);
    }
    return;
  }

  (window as any).Weglot.translate(
    { words: [{ t: 1, w: text }], languageTo: locale.value },
    function (data: any) {
      if (data && data.length && data.length !== 0) {
        translationCache[text] = data[0];
        if (callback) {
          callback(translationCache[text]);
        }
      }
    },
  );
}

function translateLabel(text: string, param1: any, param2: any, param3: any) {
  function translateCallback(translated: any) {
    let result = translated;
    if (param1) {
      const val =
        '<span class="AiTypo AiTypo--paragraph-02-bold AiTypo--color-stratos-900">' +
        param1 +
        '</span>';
      result = result.replace('%1', val);
    }
    if (param2) {
      result = result.replace('%2', param2);
    }
    if (param3) {
      result = result.replace('%3', param3);
    }
    lastUsedLabel = result;
    label.value = lastUsedLabel;
  }

  translate(text, translateCallback);
}

function refreshLabel() {
  if (!multiple.value) {
    const index = stepIndexes.has(currentStep.value)
      ? stepIndexes.get(currentStep.value)
      : 1;
    translateLabel(t('booking.step.single'), index, undefined, undefined);
    return;
  }

  switch (currentStep.value) {
    case 'room':
    case 'rate':
      translateLabel(
        t(`booking.step.multiple.${currentStep.value}`),
        currentRoomBeingFilled.value,
        bookingStore.numberOfRooms,
        currentRoomGuestCount.value,
      );
      break;

    case 'details':
      translateLabel(
        t('booking.step.multiple.details'),
        1,
        undefined,
        undefined,
      );
      break;

    case 'payment':
      translateLabel(
        t('booking.step.multiple.payment'),
        2,
        undefined,
        undefined,
      );
      break;

    default:
      break;
  }
}

function refreshTitle() {
  const baseKey = `booking.title.${multiple.value ? 'multiple' : 'single'}`;

  if (multiple.value) {
    const titleMap = new Map<string, string>([
      ['room', t(`${baseKey}.room.${currentRoomBeingFilled.value}`)],
      ['rate', t(`${baseKey}.rate.${currentRoomBeingFilled.value}`)],
    ]);

    if (titleMap.has(currentStep.value)) {
      lastUsedTitle = titleMap.get(currentStep.value);
    }
    translate(lastUsedTitle, (str: any) => {
      title.value = str;
    });
    return;
  }

  if (currentStep.value === 'payment') {
    // The title is handled by the component itself since it rely on internal state
    lastUsedTitle = '';
    title.value = '';
    return;
  }

  if (currentStep.value) {
    if (stepIndexes.has(currentStep.value)) {
      lastUsedTitle = t(`${baseKey}.${currentStep.value}`);
    }
    translate(lastUsedTitle, (str: any) => {
      title.value = str;
    });
    return;
  }

  lastUsedTitle = previousStep.value
    ? t(`${baseKey}.${previousStep.value}`)
    : '';
  translate(lastUsedTitle, (str: any) => {
    title.value = str;
  });
}

watch(locale, () => {
  translationCache = {};
  refreshLabel();
  refreshTitle();
});

watch(currentStep, (_newStep, oldStep) => {
  previousStep.value = oldStep;
  refreshLabel();
  refreshTitle();
});

watch(multiple, () => {
  refreshLabel();
  refreshTitle();
});

onMounted(() => {
  refreshLabel();
  refreshTitle();
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';

.BookingStepper-label {
  margin-bottom: constants.$margin-00;
}

.BookingStepper-title {
  margin-bottom: constants.$margin-04;
}
</style>
